import React, { ChangeEvent, useState } from 'react';
import { Card, CardBody, Table } from 'react-bootstrap';
import { Filter, Undo, AlertTriangle, Download } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import * as XLSX from 'xlsx';

type SalesAnalysisFilterState = {
    startDate: string;
    endDate: string;
    // documentType: string; //This needs to comment out for now 
    docStatus: string;
    dimension1: string;
    // dimension2: string; //This needs to comment out for now 
    division: string;
    isCancelled: string;
};


export const SalesAnalysis: React.FC = () => {

  const {fetchData, responseErrorMessage, showErrorAlert, handleCloseErrorAlert}  = useAuth();
  const stockSalesAnalysisQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SAP-SALES-ANALYSIS-REPORT/run2"}`;
  const [salesAnalysisFilter, setSalesAnalysisFilter] = useState<SalesAnalysisFilterState>({
    startDate: "",
    endDate: "",
    // documentType: "All",  //This needs to comment out for now 
    docStatus: "All",
    dimension1: "All",
    // dimension2: "All",  //This needs to comment out for now 
    division: "All",
    isCancelled: "All",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State to hold preview data
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [downloadFile, setDownloadFile] = useState<Blob | null>(null);

  const isInvalidDateRange: boolean = !!salesAnalysisFilter.startDate && !!salesAnalysisFilter.endDate && new Date(salesAnalysisFilter.startDate) > new Date(salesAnalysisFilter.endDate);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSalesAnalysisFilter((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null); //resetting error
  };

  const handleReset = () => {
    setSalesAnalysisFilter({
      startDate: "",
      endDate: "",
      // documentType: "All",  //This needs to comment out for now 
      docStatus: "All",
      dimension1: "All",
      // dimension2: "All", //This needs to comment out for now 
      division: "All",
      isCancelled: "All",
    });
    setPreviewData([]);
    setDownloadFile(null);
  };


  const handlePostFilter = () => {

    const { startDate, endDate } = salesAnalysisFilter;

    if (new Date(startDate) > new Date(endDate)) {
        return;
    }

    setLoading(true);
    setError(null);

    //comment out for now documents and dimension2 
    const formData = { 
        start_date: salesAnalysisFilter.startDate,
        end_date: salesAnalysisFilter.endDate,
        division: salesAnalysisFilter.division,
        dimension1: salesAnalysisFilter.dimension1,
        is_cancelled: salesAnalysisFilter.isCancelled,
        doc_status: salesAnalysisFilter.docStatus
      };


      fetchData(
        stockSalesAnalysisQueryUrl,
        { method: "POST", body: JSON.stringify(formData) },
        (result: any) => {
            processSalesAnalysisExcel(result);
            if (error) setError(null); //resetting error
        },
        () => {
          setLoading(false); 

          // If the global error message was set, bring it into local scope
          if (showErrorAlert && responseErrorMessage) {
          setError(responseErrorMessage);

         // Optional: If you want to clear the global one too (not required)
           setTimeout(() => {
            showErrorAlert && handleCloseErrorAlert();
           }, 0);

         }
        }
      );
  };


  // Displays a loading spinner and message during report generation
  const ReportLoader: React.FC = () => (
    <div className='reports-loading-content'>
      <div className='reports-loading-wrapper'>
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Your report is currently being generated. This process may take a few moments. Thank you for your patience.
      </div>
    </div>
  );

  // Displays an error message with an alert icon
  const ReportError: React.FC<{ message: string }> = ({ message }) => (
    <div className='reports-error-content'>
      <div className='reports-error-wrapper'>
        <AlertTriangle size={18} strokeWidth={2} />
        <span>{message}</span>
      </div>
    </div>
  );


  // Processes the Excel data and downloads it as a file
  const processSalesAnalysisExcel = async (result: any) => {

       // Step 1: Parse the Blob to preview
       const arrayBuffer = await result.arrayBuffer();
       const workbook = XLSX.read(arrayBuffer, { type: 'array' });
       const worksheet = workbook.Sheets[workbook.SheetNames[0]]; // Use the first sheet
       const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
   
       // Step 2: Set preview data to display
       setPreviewData(data);
   
       // Step 3: Save the Blob for later download
       setDownloadFile(result);  
 } 

 //Render the preview data (for example, as a table)
 const renderPreviewTable = () => {

  const headerRow = previewData[1];
  const dataRows = previewData.slice(2, 101);

  return (
    <div className='rendering-report-preview-table-soh'>
      <div className='rendering-report-preview-header'>
        <div><strong>Sales Analysis - Previewing the first 100 records....</strong></div>
        <div className='d-flex gap-2'>
          <button className='btn btn-light' title='Download Sales Analysis Report'  onClick={handleDownload} disabled={loading || !previewData.length} style={{ border: '1px solid #dbdad9' }}>
            <Download />
          </button>
          <button className='btn btn-light' title='Reset Filter' onClick={handleReset} style={{ border: '1px solid #dbdad9' }}>
            <Undo />
          </button>
        </div>
      </div>
      <div className='scrollable-table-container'>
        <div className='main-scroller'>
          <table className="table table-sm table-bordered" style={{ minWidth: '600px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headerRow.map((cell: any, index: number) => (
                  <th key={index}
                    style={{ 
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#343a40',
                      color: 'white',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      padding: '6px 10px',
                      zIndex: 2,
                      border: '1px solid #dee2e6',
                    }}
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td
                      key={cellIndex}
                      style={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        padding: '6px 10px',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const handleDownload = () => {
  if (downloadFile) {
    const formattedStartDate = salesAnalysisFilter.startDate.replace(/-/g, '_') || 'unknown_date';
    const formattedEndDate = salesAnalysisFilter.endDate.replace(/-/g, '_') || 'unknown_date';
    const rawCompany = process.env.REACT_APP_COMPANY || 'unknown_company';
    const sanitizedCompany = rawCompany.replace(/-/g, '_');
    const filename = `${sanitizedCompany}_sales_analysis_${formattedStartDate}_to_${formattedEndDate}.xlsx`;
  

    const url = URL.createObjectURL(downloadFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  }
};




  return (
    <>
      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Card.Title>{`Sales Analysis`}</Card.Title>
            {previewData.length === 0 && (
            <div style={{ display: "flex", gap: "8px" }}>
              <button className='btn btn-light' title='Apply Filter' disabled={!salesAnalysisFilter.startDate || !salesAnalysisFilter.endDate || isInvalidDateRange || loading} style={{ border: '1px solid #dbdad9' }} onClick={handlePostFilter} >
                 {loading ? (
                   <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                 ) : (
                   <Filter />
                 )}
              </button>
              <button className='btn btn-light' title='Reset Filter' onClick={handleReset} style={{ border: '1px solid #dbdad9' }}>
                <Undo />
              </button>
            </div>
             )}
          </div>   
        </Card.Header>

        <CardBody>
        {previewData.length === 0 && (
          <div className='reports-container-wrapper-v2'>
            <label className='labeled-wrapper'>
              <div className='input-labels-dates'>
                <span>Start Date</span>
                <span className='input-field-required'>* Required</span>
              </div>
              <input type="date" name="startDate" value={salesAnalysisFilter.startDate} onChange={handleChange} className='input-style' max={new Date().toISOString().split("T")[0]} />
            </label>

            {/* <label className='labeled-wrapper'>Documents
              <select className='input-style' name="documentType" value={salesAnalysisFilter.documentType} onChange={handleChange}>
                {["All", "Deliveries", "Return", "Invoices", "Credits"].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label> */}

            <label className='labeled-wrapper'>Dimension 1
              <select className='input-style' name="dimension1" value={salesAnalysisFilter.dimension1} onChange={handleChange} >
                {["All", "RTL01", "WEB01", "WS001", "HQ001"].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>

            <label className='labeled-wrapper'>Is Cancelled
              <select className='input-style' name="isCancelled" value={salesAnalysisFilter.isCancelled} onChange={handleChange}>
               {[{ label: "All", value: "All" },{ label: "Yes", value: "Y" },{ label: "No", value: "N" }].map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
               ))}
              </select>
            </label>

            <label className='labeled-wrapper'>
              <div className='input-labels-dates'>
                <span>End Date</span>
                <span className='input-field-required'>* Required</span>
              </div>
              <input type="date" name="endDate" value={salesAnalysisFilter.endDate} onChange={handleChange} className='input-style' max={new Date().toISOString().split("T")[0]} />
            </label>

            <label className='labeled-wrapper'>Doc Status
              <select className='input-style' name="docStatus" value={salesAnalysisFilter.docStatus} onChange={handleChange}>
               {[{ label: "All", value: "All" },{ label: "Open", value: "O" },{ label: "Close", value: "C" }].map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
               ))}
              </select>
            </label>

            {/* <label className='labeled-wrapper'>Dimension 2
              <select className='input-style' name="dimension2" value={salesAnalysisFilter.dimension2} onChange={handleChange}>
                <option value="All">All</option>
              </select>
            </label>  */}

            <label className='labeled-wrapper'>Division
              <select className='input-style' name="division" value={salesAnalysisFilter.division} onChange={handleChange}>
                {["All", "Wholesale", "Retail", "Web"].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>


            {isInvalidDateRange && (
              <div className='invalid-date-message'>
                <AlertTriangle size={14} strokeWidth={2} style={{ position: "relative", top: "1px" }} />
                 Start Date must be before or equal to End Date.
              </div>
            )}
          </div>
           )}  

          {loading && <ReportLoader/>}
          {error && <ReportError message={error} />}
          {previewData.length > 0 && renderPreviewTable()}
        </CardBody>
      </Card>
    </>
  );
};
